/**
 * P2-11: CraftShortcut —— craft 快捷链 +1/+25/+100/all（迁移自 WCraftShortcut，
 * js/jsx/left.jsx.js:290-412）。
 *
 * 档位逻辑逐字对齐：
 * - 实际合成数 craftRowAmt = max(craftFixed, floor(allCount × craftPercent))
 *   （left.jsx.js:306-309，其中 allCount = workshop.getCraftAllCount(name)）；
 * - 可合成判断：非 all 用 resPool.hasRes(craftPrices, craftRowAmt)（resources.js:1004）；
 *   all 用 hasMinAmt（所有原料至少够 1 次，left.jsx.js:374-387）；
 *   不足 → 渲染无 onClick、无 title、无文本的占位格（旧版"死 div"，left.jsx.js:317/324）；
 * - 文案：usePercentageConsumptionValues 开 → 显示 "1%"/"5%"/"10%"、title 为获得量；
 *   关 → 显示 "+"+获得量（plusPrefix span）、title 为百分比（left.jsx.js:321-323）；
 *   all 恒显示 $I("resources.craftTable.all")；
 * - 点击：doCraft → game.craft(name, num)（num 同 craftRowAmt 重算，left.jsx.js:389-399）；
 *   doCraftAll → game.craftAll(name)——**wood 的冬季断粮二次确认弹窗由引擎
 *   craftAll 内部处理（game.js:4165-4191），此处不重实现**；
 * - tooltip：非 all 挂 TooltipProvider（替代旧 dojo tooltip 偏移 0,60），内容为该档位
 *   逐项原料价格表（left.jsx.js:342-370）；all 无 tooltip，仅原生 title（left.jsx.js:316）；
 *   旧版 componentWillUnmount 的 dojo.destroy 清理由 Provider 的 anchor 生命周期等价承担。
 */
import { useCallback } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { TooltipAnchor } from "../../components/Tooltip/TooltipProvider";
import type { ResLike } from "./ResourceRow";

// ---------------------------------------------------------------------------
// 纯函数（导出供 P2-13 测试）
// ---------------------------------------------------------------------------

/** 对齐旧 hasMinAmt（left.jsx.js:374-387）：所有原料至少够 1 次合成 */
export function hasMinAmt(game: GamePage, recipe: { name: string }): boolean {
  let minAmt = Number.MAX_VALUE;
  const craftPrices = game.workshop.getCraftPrice(recipe); //Get price as modified by upgrades

  for (let j = 0; j < craftPrices.length; j++) {
    const totalRes = (game.resPool.get(craftPrices[j].name) as ResLike).value;
    const allAmt = Math.floor(totalRes / craftPrices[j].val);
    if (allAmt < minAmt) {
      minAmt = allAmt;
    }
  }

  return minAmt > 0 && minAmt < Number.MAX_VALUE;
}

/** 对齐旧档位换算（left.jsx.js:306-309 与 doCraft 的 389-398，两处同一公式） */
export function craftRowAmt(allCount: number, craftFixed: number, craftPercent: number): number {
  return craftFixed < allCount * craftPercent ? Math.floor(allCount * craftPercent) : craftFixed;
}

/**
 * 非 all 档位的 tooltip 内容：逐项原料价格表（left.jsx.js:342-370）。
 * 输出 HTML 结构对齐旧 dojo.create 序列化（button_tooltip > 每行 clear:both，
 * 左 res.title、右 displayValueExt(price.val × craftRowAmt)）。
 */
export function getCraftTooltipContent(
  game: GamePage,
  name: string,
  craftFixed: number,
  craftPercent: number,
): string {
  const recipe = game.workshop.getCraft(name);
  const prices = game.workshop.getCraftPrice(recipe);
  const allCount = game.workshop.getCraftAllCount(recipe.name);
  const amt = craftRowAmt(allCount, craftFixed, craftPercent);

  let html = "<div class=\"button_tooltip\">";
  for (const price of prices) {
    const res = game.resPool.get(price.name) as ResLike;
    html +=
      "<div style=\"clear: both\">" +
      "<span style=\"float: left\">" + (res.title || res.name) + "</span>" +
      "<span style=\"float: right; padding-left: 6px\">" +
      game.getDisplayValueExt(price.val * amt) +
      "</span></div>";
  }
  return html + "</div>";
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

interface ShortcutSnapshot {
  enabled: boolean;
  /** 格内文本：百分比模式 "5%" / all 档位 label；禁用为 null（旧版空格） */
  text: string | null;
  /** 非百分比模式 "+" 后的获得量文本（plusAmount），禁用为 null */
  plusAmount: string | null;
  /** 原生 title；禁用为 null */
  title: string | null;
}

function snapshotEq(a: ShortcutSnapshot | null, b: ShortcutSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.enabled === b.enabled &&
    a.text === b.text &&
    a.plusAmount === b.plusAmount &&
    a.title === b.title
  );
}

export function computeShortcutSnapshot(
  game: GamePage | null,
  name: string,
  craftFixed: number | undefined,
  craftPercent: number,
): ShortcutSnapshot | null {
  if (!game) {
    return null;
  }
  const recipe = game.workshop.getCraft(name);
  const allCount = game.workshop.getCraftAllCount(name);
  const craftRatio = game.getResCraftRatio(name);

  if (craftPercent == 1) {
    const enabled = hasMinAmt(game, recipe);
    return {
      enabled,
      text: enabled ? t("resources.craftTable.all") : null,
      plusAmount: null,
      title: enabled
        ? "+" + game.getDisplayValueExt(allCount * (1 + craftRatio), null, null, 0)
        : null,
    };
  }

  const fixed = craftFixed as number;
  const amt = craftRowAmt(allCount, fixed, craftPercent);
  const craftPrices = game.workshop.getCraftPrice(recipe);
  const enabled = !!game.resPool.hasRes(craftPrices, amt);

  if (!enabled) {
    return { enabled: false, text: null, plusAmount: null, title: null };
  }
  const gain = game.getDisplayValueExt(amt * (1 + craftRatio), null, null, 0);
  if (game.opts.usePercentageConsumptionValues) {
    return {
      enabled: true,
      text: craftPercent * 100 + "%",
      plusAmount: null,
      title: "+" + gain,
    };
  }
  return {
    enabled: true,
    text: null,
    plusAmount: gain,
    title: craftPercent * 100 + "%",
  };
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export interface CraftShortcutProps {
  name: string;
  /** all 档位（craftPercent=1）无此值 */
  craftFixed?: number;
  craftPercent: number;
}

export function CraftShortcut({ name, craftFixed, craftPercent }: CraftShortcutProps) {
  const selector = useCallback(
    (game: GamePage | null) => computeShortcutSnapshot(game, name, craftFixed, craftPercent),
    [name, craftFixed, craftPercent],
  );
  const snap = useGameTick(selector, snapshotEq);

  if (!snap) {
    return null;
  }

  const isAll = craftPercent == 1;
  const cellClass = isAll
    ? "res-cell craft-link all"
    : "res-cell craft-link craft-" + craftPercent * 100 + "pc";

  const onClick = !snap.enabled
    ? undefined
    : () => {
        const game = getGame();
        if (!game) {
          return;
        }
        if (isAll) {
          // wood 的冬季断粮二次确认弹窗在引擎 craftAll 内（game.js:4165-4191）
          game.craftAll(name);
        } else {
          // 与 render 同一公式重算（left.jsx.js:389-398）
          const allCount = game.workshop.getCraftAllCount(name);
          const num = craftRowAmt(allCount, craftFixed as number, craftPercent);
          game.craft(name, num);
        }
      };

  return (
    <TooltipAnchor
      className={cellClass}
      title={snap.title ?? undefined}
      onClick={onClick}
      // no craftAll tooltip（left.jsx.js:336-338）；非 all 挂价格明细 tooltip
      getTooltip={
        isAll
          ? undefined
          : () => {
              const game = getGame();
              return game ? getCraftTooltipContent(game, name, craftFixed as number, craftPercent) : "";
            }
      }
    >
      {snap.text}
      {snap.plusAmount !== null && (
        <>
          <span className="plusPrefix">+</span>
          {snap.plusAmount}
        </>
      )}
    </TooltipAnchor>
  );
}
